/**
 * Admin Dashboard Page
 * User and course access management with full CRUD capabilities
 */

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import { FiUsers, FiCheckCircle, FiXCircle, FiEdit2, FiTrash2, FiBook, FiPlus, FiX, FiSettings, FiCheck } from 'react-icons/fi';
import api from '../services/api';
import { AVAILABLE_COURSES, ROLE_OPTIONS, LANDING_PAGE_OPTIONS } from '../config/constants';
import ConfigEditModal from '../components/ConfigEditModal';

const AdminDashboard = () => {
  const { user } = useAuth();
  const { configs, refreshAllConfigs } = useConfig();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [courseAccessForm, setCourseAccessForm] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editModalUser, setEditModalUser] = useState(null);

  // Config management state
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [isCreatingConfig, setIsCreatingConfig] = useState(false);

  // State for dynamically loaded courses
  const [availableCourses, setAvailableCourses] = useState([]);

  // Get dynamic config values with fallbacks (courses now fetched separately)
  const roleOptions = configs.user_roles?.objectValues || ROLE_OPTIONS;
  const landingPageOptions = configs.landing_pages?.objectValues || LANDING_PAGE_OPTIONS;

  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    landingPage: '/',
    courseAccess: [],
    isActive: true
  });

  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    role: '',
    landingPage: '',
    courseAccess: [],
    isActive: true,
    password: '' // Optional: only set if changing password
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Fetch available courses from ContentItems API
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await api.get('/content-items/courses/dropdown');
        setAvailableCourses(response.data.data || []);
      } catch (error) {
        console.error('Failed to load courses:', error);
        // Fallback to hardcoded constant if API fails
        setAvailableCourses(AVAILABLE_COURSES);
        toast.error('Failed to load courses, using fallback values');
      }
    };

    fetchCourses();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [usersRes, statsRes] = await Promise.all([
        api.get('/admin/users'),
        api.get('/admin/stats')
      ]);

      setUsers(usersRes.data.users);
      setStats(statsRes.data.stats);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleEditCourseAccess = (userId, currentAccess) => {
    setEditingUser(userId);
    setCourseAccessForm(currentAccess || []);
  };

  const handleToggleCourse = (courseId) => {
    if (courseAccessForm.includes(courseId)) {
      setCourseAccessForm(courseAccessForm.filter(id => id !== courseId));
    } else {
      setCourseAccessForm([...courseAccessForm, courseId]);
    }
  };

  const handleSaveCourseAccess = async () => {
    try {
      await api.put(`/admin/users/${editingUser}/course-access`, {
        courseAccess: courseAccessForm
      });

      // Update local state
      setUsers(users.map(u => 
        u._id === editingUser 
          ? { ...u, courseAccess: courseAccessForm }
          : u
      ));

      toast.success('Course access updated successfully');
      setEditingUser(null);
      setCourseAccessForm([]);
    } catch (error) {
      console.error('Failed to update course access:', error);
      toast.error('Failed to update course access');
    }
  };

  const handleToggleActive = async (userId) => {
    try {
      const response = await api.put(`/admin/users/${userId}/toggle-active`);
      
      // Update local state
      setUsers(users.map(u => 
        u._id === userId 
          ? { ...u, isActive: response.data.user.isActive }
          : u
      ));

      toast.success(response.data.message);
    } catch (error) {
      console.error('Failed to toggle user status:', error);
      toast.error(error.response?.data?.message || 'Failed to update user status');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/admin/users/${userId}`);
      
      // Update local state
      setUsers(users.filter(u => u._id !== userId));
      
      toast.success('User deleted successfully');
      await fetchDashboardData(); // Refresh stats
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error(error.response?.data?.message || 'Failed to delete user');
    }
  };

  // Create user handlers
  const handleOpenCreateModal = () => {
    setCreateForm({
      name: '',
      email: '',
      password: '',
      role: 'user',
      landingPage: '/',
      courseAccess: [],
      isActive: true
    });
    setShowCreateModal(true);
  };

  const handleCreateFormChange = (field, value) => {
    setCreateForm(prev => ({ ...prev, [field]: value }));
    
    // Auto-set landing page based on role if not manually changed
    if (field === 'role') {
      let defaultLanding = '/';
      if (value === 'admin') defaultLanding = '/admin';
      else if (value === 'student') defaultLanding = '/teaching';
      setCreateForm(prev => ({ ...prev, landingPage: defaultLanding }));
    }
  };

  const handleToggleCourseInCreate = (courseId) => {
    setCreateForm(prev => ({
      ...prev,
      courseAccess: prev.courseAccess.includes(courseId)
        ? prev.courseAccess.filter(id => id !== courseId)
        : [...prev.courseAccess, courseId]
    }));
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!createForm.name || !createForm.email || !createForm.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const response = await api.post('/admin/users', createForm);
      setUsers([response.data.user, ...users]);
      setShowCreateModal(false);
      toast.success('User created successfully');
      await fetchDashboardData(); // Refresh stats
    } catch (error) {
      console.error('Failed to create user:', error);
      toast.error(error.response?.data?.message || 'Failed to create user');
    }
  };

  // Edit user handlers
  const handleOpenEditModal = (u) => {
    setEditModalUser(u);
    setEditForm({
      name: u.name,
      email: u.email,
      role: u.role,
      landingPage: u.landingPage || '/',
      courseAccess: u.courseAccess || [],
      isActive: u.isActive,
      password: '' // Don't pre-fill password
    });
    setShowEditModal(true);
  };

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleToggleCourseInEdit = (courseId) => {
    setEditForm(prev => ({
      ...prev,
      courseAccess: prev.courseAccess.includes(courseId)
        ? prev.courseAccess.filter(id => id !== courseId)
        : [...prev.courseAccess, courseId]
    }));
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    
    if (!editForm.name || !editForm.email) {
      toast.error('Name and email are required');
      return;
    }

    try {
      const updateData = { ...editForm };
      // Only include password if it's been set
      if (!updateData.password) {
        delete updateData.password;
      }

      const response = await api.put(`/admin/users/${editModalUser._id}`, updateData);
      setUsers(users.map(u => u._id === editModalUser._id ? response.data.user : u));
      setShowEditModal(false);
      toast.success('User updated successfully');
      await fetchDashboardData(); // Refresh stats
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error(error.response?.data?.message || 'Failed to update user');
    }
  };

  // Pending user management
  const [pendingUsers, setPendingUsers] = useState([]);
  const [showPendingModal, setShowPendingModal] = useState(false);

  const fetchPendingUsers = async () => {
    try {
      const response = await api.get('/admin/users/status/pending');
      setPendingUsers(response.data.users);
    } catch (error) {
      console.error('Failed to fetch pending users:', error);
      toast.error('Failed to load pending users');
    }
  };

  useEffect(() => {
    if (showPendingModal) {
      fetchPendingUsers();
    }
  }, [showPendingModal]);

  const handleApproveUser = async (userId) => {
    try {
      const response = await api.put(`/admin/users/${userId}/approve`);
      // Update main user list if user exists there
      setUsers(users.map(u => u._id === userId ? response.data.user : u));
      toast.success('User approved successfully');
      fetchPendingUsers();
      fetchDashboardData(); // Refresh main user list
    } catch (error) {
      console.error('Failed to approve user:', error);
      toast.error(error.response?.data?.message || 'Failed to approve user');
    }
  };

  const handleRejectUser = async (userId) => {
    if (!window.confirm('Are you sure you want to reject this user? This action cannot be undone.')) {
      return;
    }
    const reason = prompt('Enter rejection reason (optional):') || 'Rejected by administrator';
    try {
      const response = await api.put(`/admin/users/${userId}/reject`, { reason });
      // Update main user list if user exists there
      setUsers(users.map(u => u._id === userId ? response.data.user : u));
      toast.success('User rejected');
      fetchPendingUsers();
      fetchDashboardData(); // Refresh main user list
    } catch (error) {
      console.error('Failed to reject user:', error);
      toast.error(error.response?.data?.message || 'Failed to reject user');
    }
  };

  const handleChangeStatus = async (userId, newStatus) => {
    try {
      const response = await api.put(`/admin/users/${userId}`, {
        status: newStatus
      });
      setUsers(users.map(u => u._id === userId ? response.data.user : u));
      toast.success(`User status changed to ${newStatus}`);
      fetchDashboardData(); // Refresh main user list
    } catch (error) {
      console.error('Failed to change user status:', error);
      toast.error(error.response?.data?.message || 'Failed to change user status');
    }
  };

  // Config management handlers
  const handleEditConfig = (config) => {
    setEditingConfig(config);
    setIsCreatingConfig(false);
    setShowConfigModal(true);
  };

  const handleCreateConfig = () => {
    setEditingConfig(null);
    setIsCreatingConfig(true);
    setShowConfigModal(true);
  };

  const handleSaveConfig = async (configData) => {
    try {
      if (isCreatingConfig) {
        await api.post('/config', configData);
        toast.success('Configuration created successfully');
      } else {
        await api.put(`/config/${editingConfig.key}`, configData);
        toast.success('Configuration updated successfully');
      }
      setShowConfigModal(false);
      setEditingConfig(null);
      refreshAllConfigs();
    } catch (error) {
      console.error('Failed to save config:', error);
      toast.error(error.response?.data?.message || 'Failed to save configuration');
    }
  };

  const handleDeleteConfig = async (configKey, configLabel) => {
    if (!window.confirm(`Are you sure you want to delete "${configLabel}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/config/${configKey}`);
      toast.success('Configuration deleted successfully');
      refreshAllConfigs();
    } catch (error) {
      console.error('Failed to delete config:', error);
      toast.error(error.response?.data?.message || 'Failed to delete configuration');
    }
  };

  if (loading) {
    return (
      <DashboardContainer>
        <Container>
          <LoadingText>Loading dashboard...</LoadingText>
        </Container>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      <Container>
        <Header
          as={motion.div}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <WelcomeText>Welcome back, {user?.name}!</WelcomeText>
          <Title>Admin Dashboard</Title>
          <Subtitle>Manage users and course access</Subtitle>
        </Header>

        {/* Stats Section */}
        {stats && (
          <StatsGrid>
            <StatCard
              as={motion.div}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <StatIcon><FiUsers /></StatIcon>
              <StatValue>{stats.users.total}</StatValue>
              <StatLabel>Total Users</StatLabel>
              <StatSubtext>{stats.users.active} active</StatSubtext>
            </StatCard>

            <StatCard
              as={motion.div}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: 0.05 }}
            >
              <StatIcon><FiBook /></StatIcon>
              <StatValue>{stats.users.byRole.student}</StatValue>
              <StatLabel>Students Enrolled</StatLabel>
              <StatSubtext>
                {stats.users.byRole.admin} admin{stats.users.byRole.admin !== 1 ? 's' : ''}
              </StatSubtext>
            </StatCard>

            <StatCardClickable
              as={motion.div}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              onClick={() => setShowPendingModal(true)}
            >
              <StatIconWarning>⏳</StatIconWarning>
              <StatValue>{users.filter(u => u.status === 'pending').length}</StatValue>
              <StatLabel>Pending Approvals</StatLabel>
              <StatSubtext>Click to manage</StatSubtext>
            </StatCardClickable>

            <StatCard
              as={motion.div}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: 0.15 }}
            >
              <StatIcon><FiBook /></StatIcon>
              <StatValue>{stats.content.byType.blog}</StatValue>
              <StatLabel>Blog Posts</StatLabel>
              <StatSubtext>{stats.content.published} published total</StatSubtext>
            </StatCard>

            <StatCard
              as={motion.div}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <StatIcon>💼</StatIcon>
              <StatValue>{stats.content.byType.portfolio}</StatValue>
              <StatLabel>Portfolio Items</StatLabel>
              <StatSubtext>{stats.content.byType.job} job entries</StatSubtext>
            </StatCard>

            <StatCard
              as={motion.div}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: 0.25 }}
            >
              <StatIcon>🎯</StatIcon>
              <StatValue>{stats.content.byType.skill}</StatValue>
              <StatLabel>Skills Listed</StatLabel>
              <StatSubtext>{stats.content.byType.teaching} courses</StatSubtext>
            </StatCard>
          </StatsGrid>
        )}

        {/* Users Section */}
        <Section>
          <SectionHeader>
            <SectionTitle>
              User Management
              <CountBadge>{users.length} total</CountBadge>
              {users.filter(u => !u.isActive).length > 0 && (
                <CountBadge $variant="warning">
                  {users.filter(u => !u.isActive).length} inactive
                </CountBadge>
              )}
            </SectionTitle>
            <HeaderActions>
              <CreateUserButton onClick={handleOpenCreateModal}>
                <FiPlus /> Create User
              </CreateUserButton>
            </HeaderActions>
          </SectionHeader>

          <UsersTable>
            <TableHeader>
              <HeaderCell>User</HeaderCell>
              <HeaderCell>Email</HeaderCell>
              <HeaderCell>Role</HeaderCell>
              <HeaderCell>Landing Page</HeaderCell>
              <HeaderCell>Course Access</HeaderCell>
              <HeaderCell>Status</HeaderCell>
              <HeaderCell>Actions</HeaderCell>
            </TableHeader>

            <TableBody>
              {users.map((u, index) => (
                <TableRow
                  key={u._id}
                  as={motion.div}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <Cell>
                    <UserInfo>
                      <UserStatusIndicator 
                        isLoggedIn={u.lastLogin && (Date.now() - new Date(u.lastLogin).getTime()) < 3600000} // Logged in within last hour
                        hasIssue={u.status === 'pending' || u.status === 'rejected'}
                      />
                      <UserAvatar>{u.name.charAt(0).toUpperCase()}</UserAvatar>
                      <UserName>{u.name}</UserName>
                    </UserInfo>
                  </Cell>
                  <Cell>
                    <UserEmail>{u.email}</UserEmail>
                  </Cell>
                  <Cell>
                    <RoleBadge role={u.role}>{u.role}</RoleBadge>
                  </Cell>
                  <Cell>
                    <LandingPageBadge>{u.landingPage || '/'}</LandingPageBadge>
                  </Cell>
                  <Cell>
                    {editingUser === u._id ? (
                      <CourseAccessForm>
                        {availableCourses.map(course => (
                          <CourseCheckbox key={course.id}>
                            <input
                              type="checkbox"
                              checked={courseAccessForm.includes(course.id)}
                              onChange={() => handleToggleCourse(course.id)}
                            />
                            <span>{course.name}</span>
                          </CourseCheckbox>
                        ))}
                        <FormButtons>
                          <SaveButton onClick={handleSaveCourseAccess}>Save</SaveButton>
                          <CancelButton onClick={() => setEditingUser(null)}>Cancel</CancelButton>
                        </FormButtons>
                      </CourseAccessForm>
                    ) : (
                      <CourseList>
                        {u.courseAccess && u.courseAccess.length > 0 ? (
                          u.courseAccess.map(course => (
                            <CourseBadge key={course}>{course}</CourseBadge>
                          ))
                        ) : (
                          <NoAccess>No course access</NoAccess>
                        )}
                        <EditAccessButton onClick={() => handleEditCourseAccess(u._id, u.courseAccess)}>
                          <FiEdit2 /> Edit
                        </EditAccessButton>
                      </CourseList>
                    )}
                  </Cell>
                  <Cell>
                    <StatusContainer>
                      <StatusBadge active={u.isActive}>
                        {u.isActive ? (
                          <><FiCheckCircle /> Active</>
                        ) : (
                          <><FiXCircle /> Inactive</>
                        )}
                      </StatusBadge>
                      {u._id !== user?.id && (
                        <StatusSelect
                          value={u.status || 'pending'}
                          onChange={(e) => handleChangeStatus(u._id, e.target.value)}
                          title="Change user status"
                        >
                          <option value="pending">Pending</option>
                          <option value="approved">Approved</option>
                          <option value="rejected">Rejected</option>
                        </StatusSelect>
                      )}
                    </StatusContainer>
                  </Cell>
                  <Cell>
                    <ActionButtons>
                      <ActionButton
                        onClick={() => handleOpenEditModal(u)}
                        title="Edit user"
                      >
                        <FiEdit2 />
                      </ActionButton>
                      {u.status === 'pending' && u._id !== user?.id && (
                        <>
                          <ActionButton
                            onClick={() => handleApproveUser(u._id)}
                            title="Approve user"
                            style={{ color: '#10b981' }}
                          >
                            <FiCheck />
                          </ActionButton>
                          <ActionButton
                            onClick={() => handleRejectUser(u._id)}
                            title="Reject user"
                            style={{ color: '#ef4444' }}
                          >
                            <FiX />
                          </ActionButton>
                        </>
                      )}
                      <ActionButton
                        onClick={() => handleToggleActive(u._id)}
                        disabled={u._id === user?.id}
                        title={u.isActive ? 'Deactivate user' : 'Activate user'}
                      >
                        {u.isActive ? <FiXCircle /> : <FiCheckCircle />}
                      </ActionButton>
                      {u.role !== 'admin' && (
                        <ActionButton
                          danger
                          onClick={() => handleDeleteUser(u._id, u.name)}
                          disabled={u._id === user?.id}
                          title="Delete user"
                        >
                          <FiTrash2 />
                        </ActionButton>
                      )}
                    </ActionButtons>
                  </Cell>
                </TableRow>
              ))}
            </TableBody>
          </UsersTable>
        </Section>

        {/* Configuration Settings Section */}
        <Section>
          <SectionHeader>
            <SectionTitle>
              Configuration Settings
              <CountBadge>{Object.keys(configs).length} configs</CountBadge>
            </SectionTitle>
            <HeaderActions>
              <CreateUserButton onClick={handleCreateConfig}>
                <FiPlus /> Add Config
              </CreateUserButton>
            </HeaderActions>
          </SectionHeader>

          <ConfigGrid>
            {Object.values(configs).map((config, index) => (
              <ConfigCard
                key={config.key}
                as={motion.div}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <ConfigHeader>
                  <ConfigIcon>
                    <FiSettings />
                  </ConfigIcon>
                  <ConfigInfo>
                    <ConfigTitle>{config.label}</ConfigTitle>
                    {config.description && (
                      <ConfigDescription>{config.description}</ConfigDescription>
                    )}
                  </ConfigInfo>
                </ConfigHeader>

                <ConfigContent>
                  <ConfigType>
                    {config.type === 'string_array' ? '📝 Simple List' : '🔷 Object Array'}
                  </ConfigType>
                  
                  <ConfigItems>
                    {config.type === 'string_array' ? (
                      <ItemsList>
                        {config.values.slice(0, 5).map((val, idx) => (
                          <ItemBadge key={idx}>{val}</ItemBadge>
                        ))}
                        {config.values.length > 5 && (
                          <ItemBadge $more>+{config.values.length - 5} more</ItemBadge>
                        )}
                      </ItemsList>
                    ) : (
                      <ItemsList>
                        {config.objectValues.slice(0, 3).map((obj, idx) => (
                          <ItemBadge key={idx}>
                            {obj.label || obj.name || obj.value || `Item ${idx + 1}`}
                          </ItemBadge>
                        ))}
                        {config.objectValues.length > 3 && (
                          <ItemBadge $more>+{config.objectValues.length - 3} more</ItemBadge>
                        )}
                      </ItemsList>
                    )}
                  </ConfigItems>

                  <ConfigStats>
                    {config.type === 'string_array' 
                      ? `${config.values.length} items`
                      : `${config.objectValues.length} objects`
                    }
                  </ConfigStats>
                </ConfigContent>

                <ConfigActions>
                  <ConfigActionButton onClick={() => handleEditConfig(config)}>
                    <FiEdit2 /> Edit
                  </ConfigActionButton>
                  <ConfigActionButton 
                    $danger 
                    onClick={() => handleDeleteConfig(config.key, config.label)}
                  >
                    <FiTrash2 /> Delete
                  </ConfigActionButton>
                </ConfigActions>
              </ConfigCard>
            ))}

            {Object.keys(configs).length === 0 && (
              <EmptyState>
                <EmptyStateIcon>⚙️</EmptyStateIcon>
                <EmptyStateText>No configurations yet</EmptyStateText>
                <EmptyStateSubtext>
                  Create your first configuration to get started
                </EmptyStateSubtext>
              </EmptyState>
            )}
          </ConfigGrid>
        </Section>

        {/* Config Edit/Create Modal */}
        <ConfigEditModal
          isOpen={showConfigModal}
          onClose={() => {
            setShowConfigModal(false);
            setEditingConfig(null);
            setIsCreatingConfig(false);
          }}
          config={editingConfig}
          onSave={handleSaveConfig}
          isCreating={isCreatingConfig}
        />

        {/* Create User Modal */}
        <AnimatePresence>
          {showCreateModal && (
            <ModalOverlay
              as={motion.div}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowCreateModal(false)}
            >
              <ModalContent
                as={motion.div}
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                onClick={(e) => e.stopPropagation()}
              >
                <ModalHeader>
                  <ModalTitle>Create New User</ModalTitle>
                  <CloseButton onClick={() => setShowCreateModal(false)}>
                    <FiX />
                  </CloseButton>
                </ModalHeader>

                <ModalForm onSubmit={handleCreateUser}>
                  <FormRow>
                    <FormGroup>
                      <Label>Name *</Label>
                      <Input
                        type="text"
                        value={createForm.name}
                        onChange={(e) => handleCreateFormChange('name', e.target.value)}
                        required
                      />
                    </FormGroup>
                  </FormRow>

                  <FormRow>
                    <FormGroup>
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={createForm.email}
                        onChange={(e) => handleCreateFormChange('email', e.target.value)}
                        required
                      />
                    </FormGroup>
                  </FormRow>

                  <FormRow>
                    <FormGroup>
                      <Label>Password *</Label>
                      <Input
                        type="password"
                        value={createForm.password}
                        onChange={(e) => handleCreateFormChange('password', e.target.value)}
                        required
                        minLength="6"
                      />
                    </FormGroup>
                  </FormRow>

                  <FormRow>
                    <FormGroup>
                      <Label>Role</Label>
                      <Select
                        value={createForm.role}
                        onChange={(e) => handleCreateFormChange('role', e.target.value)}
                      >
                        {roleOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </Select>
                    </FormGroup>

                    <FormGroup>
                      <Label>Landing Page</Label>
                      <Select
                        value={createForm.landingPage}
                        onChange={(e) => handleCreateFormChange('landingPage', e.target.value)}
                      >
                        {landingPageOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </Select>
                    </FormGroup>
                  </FormRow>

                  <FormGroup>
                    <Label>Course Access</Label>
                    <CheckboxGroup>
                      {availableCourses.map(course => (
                        <CheckboxLabel key={course.id}>
                          <input
                            type="checkbox"
                            checked={createForm.courseAccess.includes(course.id)}
                            onChange={() => handleToggleCourseInCreate(course.id)}
                          />
                          <span>{course.name}</span>
                        </CheckboxLabel>
                      ))}
                    </CheckboxGroup>
                  </FormGroup>

                  <FormGroup>
                    <CheckboxLabel>
                      <input
                        type="checkbox"
                        checked={createForm.isActive}
                        onChange={(e) => handleCreateFormChange('isActive', e.target.checked)}
                      />
                      <span>Account Active</span>
                    </CheckboxLabel>
                  </FormGroup>

                  <ModalActions>
                    <CancelButton type="button" onClick={() => setShowCreateModal(false)}>
                      Cancel
                    </CancelButton>
                    <SubmitButton type="submit">Create User</SubmitButton>
                  </ModalActions>
                </ModalForm>
              </ModalContent>
            </ModalOverlay>
          )}
        </AnimatePresence>

        {/* Edit User Modal */}
        <AnimatePresence>
          {showEditModal && editModalUser && (
            <ModalOverlay
              as={motion.div}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowEditModal(false)}
            >
              <ModalContent
                as={motion.div}
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                onClick={(e) => e.stopPropagation()}
              >
                <ModalHeader>
                  <ModalTitle>Edit User: {editModalUser.name}</ModalTitle>
                  <CloseButton onClick={() => setShowEditModal(false)}>
                    <FiX />
                  </CloseButton>
                </ModalHeader>

                <ModalForm onSubmit={handleUpdateUser}>
                  <FormRow>
                    <FormGroup>
                      <Label>Name *</Label>
                      <Input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => handleEditFormChange('name', e.target.value)}
                        required
                      />
                    </FormGroup>
                  </FormRow>

                  <FormRow>
                    <FormGroup>
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={editForm.email}
                        onChange={(e) => handleEditFormChange('email', e.target.value)}
                        required
                      />
                    </FormGroup>
                  </FormRow>

                  <FormRow>
                    <FormGroup>
                      <Label>New Password (leave blank to keep current)</Label>
                      <Input
                        type="password"
                        value={editForm.password}
                        onChange={(e) => handleEditFormChange('password', e.target.value)}
                        minLength="6"
                        placeholder="Leave blank to keep current password"
                      />
                    </FormGroup>
                  </FormRow>

                  <FormRow>
                    <FormGroup>
                      <Label>Role</Label>
                      <Select
                        value={editForm.role}
                        onChange={(e) => handleEditFormChange('role', e.target.value)}
                        disabled={editModalUser._id === user?.id}
                      >
                        {roleOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </Select>
                    </FormGroup>

                    <FormGroup>
                      <Label>Landing Page</Label>
                      <Select
                        value={editForm.landingPage}
                        onChange={(e) => handleEditFormChange('landingPage', e.target.value)}
                      >
                        {landingPageOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </Select>
                    </FormGroup>
                  </FormRow>

                  <FormGroup>
                    <Label>Course Access</Label>
                    <CheckboxGroup>
                      {availableCourses.map(course => (
                        <CheckboxLabel key={course.id}>
                          <input
                            type="checkbox"
                            checked={editForm.courseAccess.includes(course.id)}
                            onChange={() => handleToggleCourseInEdit(course.id)}
                          />
                          <span>{course.name}</span>
                        </CheckboxLabel>
                      ))}
                    </CheckboxGroup>
                  </FormGroup>

                  <FormGroup>
                    <CheckboxLabel>
                      <input
                        type="checkbox"
                        checked={editForm.isActive}
                        onChange={(e) => handleEditFormChange('isActive', e.target.checked)}
                        disabled={editModalUser._id === user?.id}
                      />
                      <span>Account Active</span>
                    </CheckboxLabel>
                  </FormGroup>

                  <ModalActions>
                    <CancelButton type="button" onClick={() => setShowEditModal(false)}>
                      Cancel
                    </CancelButton>
                    <SubmitButton type="submit">Update User</SubmitButton>
                  </ModalActions>
                </ModalForm>
              </ModalContent>
            </ModalOverlay>
          )}
        </AnimatePresence>

        {/* Pending Users Modal */}
        <AnimatePresence>
          {showPendingModal && (
            <ModalOverlay
              as={motion.div}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowPendingModal(false)}
            >
              <ModalContent
                as={motion.div}
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                <ModalHeader>
                  <ModalTitle>Pending User Approvals</ModalTitle>
                  <CloseButton onClick={() => setShowPendingModal(false)}>
                    <FiX />
                  </CloseButton>
                </ModalHeader>
                
                <PendingModalBody>
                  {pendingUsers.length === 0 ? (
                    <EmptyState>
                      No pending users
                    </EmptyState>
                  ) : (
                    <PendingUsersList>
                      {pendingUsers.map((u) => (
                        <PendingUserCard key={u._id}>
                          <PendingUserInfo>
                            <PendingUserName>{u.name}</PendingUserName>
                            <PendingUserEmail>
                              {u.email}
                            </PendingUserEmail>
                            <PendingUserMeta>
                              Registered: {new Date(u.createdAt).toLocaleDateString()}
                            </PendingUserMeta>
                            {u.googleId && (
                              <PendingUserOAuth>
                                🔗 Google OAuth
                              </PendingUserOAuth>
                            )}
                          </PendingUserInfo>
                          <PendingUserActions>
                            <ApproveButton
                              onClick={() => handleApproveUser(u._id)}
                              title="Approve"
                            >
                              <FiCheckCircle />
                            </ApproveButton>
                            <RejectButton
                              onClick={() => handleRejectUser(u._id)}
                              title="Reject"
                            >
                              <FiXCircle />
                            </RejectButton>
                          </PendingUserActions>
                        </PendingUserCard>
                      ))}
                    </PendingUsersList>
                  )}
                </PendingModalBody>
              </ModalContent>
            </ModalOverlay>
          )}
        </AnimatePresence>
      </Container>
    </DashboardContainer>
  );
};

// Styled Components
const DashboardContainer = styled.div`
  min-height: 100vh;
  padding: 4rem 0;
  background: var(--bg-primary);
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 4rem;
  font-size: 1.25rem;
  color: var(--text-secondary);
`;

const Header = styled.div`
  margin-bottom: 3rem;
`;

const WelcomeText = styled.p`
  color: var(--accent-primary);
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

const Title = styled.h1`
  font-size: clamp(2rem, 5vw, 3rem);
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  font-size: 1.125rem;
  color: var(--text-secondary);
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
`;

const StatCard = styled.div`
  background: var(--bg-secondary);
  padding: 1.25rem;
  border-radius: 1rem;
  border: 1px solid var(--border-color);
  text-align: center;
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  background: var(--accent-gradient);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  color: white;
  margin: 0 auto 0.75rem;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 1rem;
  color: var(--text-secondary);
  margin-bottom: 0.25rem;
`;

const StatSubtext = styled.div`
  font-size: 0.875rem;
  color: var(--text-tertiary);
`;

const Section = styled.section`
  margin-bottom: 3rem;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.75rem;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$variant === 'warning' 
    ? 'rgba(255, 193, 7, 0.15)' 
    : 'rgba(0, 212, 255, 0.15)'};
  color: ${props => props.$variant === 'warning' 
    ? '#ffc107' 
    : 'var(--accent)'};
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
  border: 1px solid ${props => props.$variant === 'warning' 
    ? 'rgba(255, 193, 7, 0.3)' 
    : 'rgba(0, 212, 255, 0.3)'};
`;

const UsersTable = styled.div`
  background: var(--bg-secondary);
  border-radius: 1rem;
  border: 1px solid var(--border-color);
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 2fr 1fr 1.5fr 2fr 1fr 1.5fr;
  padding: 1rem 1.5rem;
  background: var(--bg-tertiary);
  border-bottom: 1px solid var(--border-color);
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-secondary);

  @media (max-width: 1024px) {
    display: none;
  }
`;

const HeaderCell = styled.div`
  display: flex;
  align-items: center;
`;

const TableBody = styled.div``;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 2fr 1fr 1.5fr 2fr 1fr 1.5fr;
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  transition: background 0.2s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--bg-tertiary);
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const Cell = styled.div`
  display: flex;
  align-items: center;
  color: var(--text-primary);

  @media (max-width: 1024px) {
    justify-content: flex-start;
  }
`;

const UserStatusIndicator = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => {
    if (props.hasIssue) return '#ef4444'; // Red for issues
    if (props.isLoggedIn) return '#10b981'; // Green for logged in
    return 'transparent'; // No indicator
  }};
  flex-shrink: 0;
  margin-right: ${props => (props.isLoggedIn || props.hasIssue) ? '0.5rem' : '0'};
  transition: all 0.2s ease;
  
  ${props => (props.isLoggedIn || props.hasIssue) && `
    box-shadow: 0 0 0 2px ${props.hasIssue ? 'rgba(239, 68, 68, 0.2)' : 'rgba(16, 185, 129, 0.2)'};
  `}
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--accent-gradient);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 1.125rem;
`;

const UserName = styled.div`
  font-weight: 500;
`;

const UserEmail = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

const RoleBadge = styled.span`
  padding: 0.375rem 0.875rem;
  border-radius: 2rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => {
    if (props.role === 'admin') return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
    if (props.role === 'student') return 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)';
    return 'var(--bg-tertiary)';
  }};
  color: ${props => (props.role === 'admin' || props.role === 'student') ? 'white' : 'var(--text-secondary)'};
`;

const LandingPageBadge = styled.span`
  padding: 0.375rem 0.75rem;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  border-radius: 0.5rem;
  font-size: 0.75rem;
  font-family: 'Courier New', monospace;
`;

const CourseList = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const CourseBadge = styled.span`
  padding: 0.375rem 0.75rem;
  background: var(--accent-gradient);
  color: white;
  border-radius: 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
`;

const NoAccess = styled.span`
  font-size: 0.875rem;
  color: var(--text-tertiary);
  font-style: italic;
`;

const EditAccessButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  background: var(--bg-tertiary);
  color: var(--accent-primary);
  border-radius: 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    background: var(--accent-primary);
    color: white;
  }
`;

const CourseAccessForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
`;

const CourseCheckbox = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  cursor: pointer;

  input {
    cursor: pointer;
  }

  span {
    color: var(--text-primary);
  }
`;

const FormButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const SaveButton = styled.button`
  padding: 0.5rem 1rem;
  background: var(--accent-gradient);
  color: white;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-2px);
  }
`;

const CancelButton = styled.button`
  padding: 0.5rem 1rem;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  transition: all 0.2s;

  &:hover {
    background: var(--bg-quaternary);
  }
`;

const StatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-start;
`;

const StatusSelect = styled.select`
  padding: 0.25rem 0.5rem;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 0.75rem;
  cursor: pointer;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
  }

  &:hover {
    border-color: var(--accent-primary);
  }
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.875rem;
  border-radius: 2rem;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => props.active 
    ? 'rgba(16, 185, 129, 0.1)'
    : 'rgba(239, 68, 68, 0.1)'
  };
  color: ${props => props.active 
    ? 'rgb(16, 185, 129)'
    : 'rgb(239, 68, 68)'
  };
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  background: ${props => props.danger ? 'rgba(239, 68, 68, 0.1)' : 'var(--bg-tertiary)'};
  color: ${props => props.danger ? 'rgb(239, 68, 68)' : 'var(--text-primary)'};
  transition: all 0.2s;
  font-size: 1rem;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    background: ${props => props.danger ? 'rgba(239, 68, 68, 0.2)' : 'var(--accent-primary)'};
    color: ${props => props.danger ? 'rgb(239, 68, 68)' : 'white'};
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const CreateUserButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: var(--accent-gradient);
  color: white;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 0.875rem;
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-2px);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
  overflow-y: auto;
`;

const ModalContent = styled.div`
  background: var(--bg-secondary);
  border-radius: 1rem;
  border: 1px solid var(--border-color);
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid var(--border-color);
  position: sticky;
  top: 0;
  background: var(--bg-secondary);
  z-index: 10;
`;

const ModalTitle = styled.h3`
  font-size: 1.5rem;
  color: var(--text-primary);
`;

const CloseButton = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  background: transparent;
  color: var(--text-secondary);
  font-size: 1.25rem;
  transition: all 0.2s;

  &:hover {
    background: var(--bg-tertiary);
    color: var(--text-primary);
  }
`;

const ModalForm = styled.form`
  padding: 2rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  color: var(--text-primary);
  font-size: 0.875rem;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    background: var(--bg-primary);
  }

  &::placeholder {
    color: var(--text-tertiary);
  }
`;

const Select = styled.select`
  padding: 0.75rem 1rem;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  color: var(--text-primary);
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    background: var(--bg-primary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  background: var(--bg-tertiary);
  border-radius: 0.5rem;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: var(--text-primary);

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border-color);
`;

const SubmitButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: var(--accent-gradient);
  color: white;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 0.875rem;
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StatCardClickable = styled(StatCard)`
  cursor: pointer;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }
`;

const StatIconWarning = styled(StatIcon)`
  color: #f59e0b;
`;

const PendingModalBody = styled.div`
  padding: 1.5rem;
`;

const PendingUsersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const PendingUserCard = styled.div`
  padding: 1rem;
  background: var(--bg-secondary);
  border-radius: 0.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PendingUserInfo = styled.div`
  flex: 1;
`;

const PendingUserName = styled.div`
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const PendingUserEmail = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

const PendingUserMeta = styled.div`
  font-size: 0.75rem;
  color: var(--text-tertiary);
  margin-top: 0.25rem;
`;

const PendingUserOAuth = styled.div`
  font-size: 0.75rem;
  color: var(--accent-primary);
  margin-top: 0.25rem;
`;

const PendingUserActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ApproveButton = styled(ActionButton)`
  background: #10b981;
  color: white;
  padding: 0.5rem;
  
  &:hover {
    background: #059669;
  }
`;

const RejectButton = styled(ActionButton)`
  background: #ef4444;
  color: white;
  padding: 0.5rem;
  
  &:hover {
    background: #dc2626;
  }
`;

// Configuration Settings Styled Components
const ConfigGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
`;

const ConfigCard = styled.div`
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 1rem;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  transition: all 0.2s;

  &:hover {
    border-color: var(--accent);
    transform: translateY(-2px);
  }
`;

const ConfigHeader = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;
`;

const ConfigIcon = styled.div`
  width: 40px;
  height: 40px;
  background: var(--accent-gradient);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.25rem;
  flex-shrink: 0;
`;

const ConfigInfo = styled.div`
  flex: 1;
`;

const ConfigTitle = styled.h3`
  font-size: 1.125rem;
  color: var(--text-primary);
  margin: 0 0 0.25rem 0;
`;

const ConfigDescription = styled.p`
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin: 0;
  line-height: 1.4;
`;

const ConfigContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ConfigType = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
  background: var(--bg-tertiary);
  padding: 0.25rem 0.75rem;
  border-radius: 8px;
  width: fit-content;
`;

const ConfigItems = styled.div`
  min-height: 60px;
`;

const ItemsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const ItemBadge = styled.span`
  background: ${props => props.$more ? 'var(--bg-tertiary)' : 'rgba(0, 212, 255, 0.1)'};
  color: ${props => props.$more ? 'var(--text-secondary)' : 'var(--accent)'};
  border: 1px solid ${props => props.$more ? 'var(--border-color)' : 'rgba(0, 212, 255, 0.3)'};
  padding: 0.375rem 0.75rem;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 500;
  white-space: nowrap;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ConfigStats = styled.div`
  font-size: 0.875rem;
  color: var(--text-tertiary);
  font-weight: 600;
`;

const ConfigActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: auto;
  padding-top: 0.75rem;
  border-top: 1px solid var(--border-color);
`;

const ConfigActionButton = styled.button`
  flex: 1;
  background: ${props => props.$danger ? 'rgba(255, 59, 48, 0.1)' : 'rgba(0, 212, 255, 0.1)'};
  border: 1px solid ${props => props.$danger ? 'rgba(255, 59, 48, 0.3)' : 'rgba(0, 212, 255, 0.3)'};
  color: ${props => props.$danger ? '#ff3b30' : 'var(--accent)'};
  padding: 0.625rem 1rem;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$danger ? 'rgba(255, 59, 48, 0.2)' : 'rgba(0, 212, 255, 0.2)'};
    transform: translateY(-1px);
  }
`;

const EmptyState = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 3rem 1rem;
  background: var(--bg-secondary);
  border: 2px dashed var(--border-color);
  border-radius: 1rem;
`;

const EmptyStateIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const EmptyStateText = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const EmptyStateSubtext = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

export default AdminDashboard;
